import React, { useCallback, useMemo, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { Grid, Paper, SelectChangeEvent } from '@mui/material';
import { useResizeDetector } from 'react-resize-detector';
import { useGetSearchLabels } from 'modules/Insights/hooks/useGetSearchLabels';
import { useSupervisorInsightContext } from 'modules/Insights/Notes/SupervisorInsightContextProvider';
import { OrgAnalyticsDimensionType, OrgAnalyticsContext } from 'graphql/types';
import { useGetSupervisorAnalyticsData } from 'modules/Insights/Notes/hooks/useGetSupervisorAnalyticsData';
import SourceDropDown from 'modules/Insights/components/SourceByTimeChart/SourceDropDown';
import { useMessages } from 'modules/intl';
import UtilizationChart from './UtilizationChart';
import {
  dataType,
  productivitycolorSet,
  transformIntoUtilizationChartData,
  utilizationColorSet,
  DropdownLabel
} from './utils';
import UtilizationChartSkeleton from './UtilizationChartSkeleton';
import UtilizationChartNoData from './UtilizationChartNoData';
import Header from './Header';
import ProductivityChart from './ProductivityChart';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(2)
  }
}));

const DropdownLabelName = [
  `insights.timeUtilizationChart.${DropdownLabel.supervisorUtilization}`,
  `insights.timeUtilizationChart.${DropdownLabel.supervisorProductivity}`
];

const SupervisorUtilizationChart: React.FC = () => {
  const [checked, setChecked] = useState(false);
  const labelValues = useGetSearchLabels();
  const classes = useStyles();
  const optionNames = useMessages(DropdownLabelName);
  const dropDownOptions = useMemo(
    () => [
      {
        id: DropdownLabel.supervisorUtilization,
        name:
          optionNames[
            `insights.timeUtilizationChart.${DropdownLabel.supervisorUtilization}`
          ]
      },
      {
        id: DropdownLabel.supervisorProductivity,
        name:
          optionNames[
            `insights.timeUtilizationChart.${DropdownLabel.supervisorProductivity}`
          ]
      }
    ],
    [optionNames]
  );
  const [selectvalue, setSelectValue] = useState<string>(
    DropdownLabel.supervisorUtilization
  );
  const [currentChartData, setCurrentChartData] = useState<dataType[]>([]);
  const isUtilizationSelected = useMemo(() => {
    return selectvalue === DropdownLabel.supervisorUtilization;
  }, [selectvalue]);

  const { dimesionType, colorSet } = useMemo(() => {
    if (!isUtilizationSelected) {
      return {
        dimesionType: OrgAnalyticsDimensionType.MetricsByTeamProductivity,
        colorSet: productivitycolorSet
      };
    } else {
      return {
        dimesionType: OrgAnalyticsDimensionType.MetricsByNoteUtilization,
        colorSet: utilizationColorSet
      };
    }
  }, [isUtilizationSelected]);

  const { data: rData, loading, isEmpty } = useGetSupervisorAnalyticsData(
    dimesionType,
    labelValues,
    OrgAnalyticsContext.Supervisor
  );

  const {
    currentPeriod,
    selectedViewOptionSupervisor
  } = useSupervisorInsightContext();
  const tData = useMemo(
    () =>
      transformIntoUtilizationChartData(
        rData?.orgAnalytics?.analyticsResult.items,
        checked,
        currentPeriod,
        isUtilizationSelected,
        colorSet
      ),
    [
      checked,
      colorSet,
      currentPeriod,
      rData?.orgAnalytics?.analyticsResult.items,
      isUtilizationSelected
    ]
  );

  const handleCheckBoxChange = useCallback((event) => {
    setChecked(event.target.checked);
  }, []);

  const onSelectValueChange = useCallback((event: SelectChangeEvent) => {
    setSelectValue(event.target.value);
  }, []);

  const headerNameComponent = useMemo(
    () => (
      <SourceDropDown
        options={dropDownOptions}
        onChange={onSelectValueChange}
        value={selectvalue}
        disabled={false}
      />
    ),
    [dropDownOptions, onSelectValueChange, selectvalue]
  );
  const { width, ref: chartRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 0
  });

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'my-utilization-chart-container'}
      ref={chartRef}
    >
      <Grid container>
        <Header
          currentData={currentChartData}
          headerNameComponent={headerNameComponent}
          checked={checked}
          handleCheckBoxChange={handleCheckBoxChange}
          disabled={loading || isEmpty}
          loading={loading}
          isEmpty={isEmpty}
        />
        {loading ? (
          <UtilizationChartSkeleton />
        ) : isEmpty ? (
          <UtilizationChartNoData
            isUtilizationSelected={isUtilizationSelected}
          />
        ) : (
          <>
            {isUtilizationSelected && (
              <UtilizationChart
                selectedViewOptionSupervisor={selectedViewOptionSupervisor}
                width={width}
                tData={tData}
                setCurrentChartData={setCurrentChartData}
              />
            )}
            {!isUtilizationSelected && (
              <ProductivityChart
                selectedViewOptionSupervisor={selectedViewOptionSupervisor}
                width={width}
                tData={tData}
                setCurrentChartData={setCurrentChartData}
              />
            )}
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default React.memo(SupervisorUtilizationChart);
